import type { MailSignaturePreview, PublicMailIdentity } from '@embertime/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { AlertTriangle, Bold, Italic, Link2 } from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { api } from '../../lib/api'
import { notify } from '../../lib/notify'
import { Button } from '../ui/button'
import { SidePanel } from '../ui/side-panel'
import { IdentityKindIcon, identitiesQueryKey, identityStatusBadge } from './identity-list'

export function RichTextEditor({
  value,
  onChange,
  label,
  hint,
  children,
  minHeight = 'min-h-16',
}: {
  value: string
  onChange: (html: string) => void
  label: string
  hint?: string
  children?: ReactNode
  minHeight?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value
  }, [value])
  const command = (name: 'bold' | 'italic' | 'createLink') => {
    ref.current?.focus()
    if (name === 'createLink') {
      const href = window.prompt('Link-Adresse (https:// oder mailto:)')
      if (!href || !/^(https?:\/\/|mailto:)[^\s<>"']+$/i.test(href)) return
      document.execCommand(name, false, href)
    } else document.execCommand(name)
    onChange(ref.current?.innerHTML ?? '')
  }
  return (
    <div className="overflow-hidden rounded-xl border border-border-default bg-bg-overlay/40">
      <div
        className="flex items-center gap-1 border-b border-border-subtle px-2 py-1"
        role="toolbar"
        aria-label="Textformatierung"
      >
        <button
          type="button"
          className="no-min-tap rounded-lg bg-accent/10 p-2 text-accent hover:bg-accent/15"
          aria-label="Fett"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => command('bold')}
        >
          <Bold size={18} />
        </button>
        <button
          type="button"
          className="no-min-tap rounded-lg p-2 text-text-secondary hover:bg-bg-overlay"
          aria-label="Kursiv"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => command('italic')}
        >
          <Italic size={18} />
        </button>
        <button
          type="button"
          className="no-min-tap rounded-lg p-2 text-text-secondary hover:bg-bg-overlay"
          aria-label="Link"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => command('createLink')}
        >
          <Link2 size={18} />
        </button>
        {hint && (
          <span className="ml-1 border-l border-border-default pl-2 text-[11px] text-text-tertiary">
            {hint}
          </span>
        )}
      </div>
      {/* biome-ignore lint/a11y/useSemanticElements: contentEditable rich text cannot use textarea */}
      <div
        ref={ref}
        role="textbox"
        tabIndex={0}
        aria-label={label}
        aria-multiline="true"
        contentEditable
        suppressContentEditableWarning
        className={`${minHeight} px-3 py-3 text-sm leading-[1.4] outline-none`}
        onInput={(event) => onChange(event.currentTarget.innerHTML)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || event.nativeEvent.isComposing) return
          event.preventDefault()
          document.execCommand('insertLineBreak')
          onChange(ref.current?.innerHTML ?? '')
        }}
        onPaste={(event) => {
          event.preventDefault()
          document.execCommand('insertText', false, event.clipboardData.getData('text/plain'))
        }}
      />
      {children}
    </div>
  )
}

export function SignaturePreview({ signature }: { signature: MailSignaturePreview | undefined }) {
  if (!signature || signature.mode !== 'auto') return null
  return (
    <div className="mx-3 border-t border-dashed border-border-default pb-3 pt-3 text-xs leading-relaxed">
      <p className="mb-1 inline-block rounded-full border border-dashed border-border-default px-2 text-[10px] text-text-tertiary">
        Pflichtangaben · automatisch
      </p>
      {signature.requiredLines.map((line) => (
        <p
          key={line.label}
          className={line.missing ? 'italic text-warning' : 'text-text-secondary'}
        >
          {line.value}
        </p>
      ))}
    </div>
  )
}

export function IdentitySignatureSheet({
  identity,
  accountId,
  onClose,
}: {
  identity: PublicMailIdentity
  accountId: string
  onClose: () => void
}) {
  const qc = useQueryClient()
  const badge = identityStatusBadge(identity)
  const [mode, setMode] = useState<'auto' | 'custom'>(identity.signatureMode ?? 'auto')
  const [greeting, setGreeting] = useState(identity.signatureGreetingHtml ?? '')
  const [companyId, setCompanyId] = useState(identity.companyId ?? '')
  const [preview, setPreview] = useState(identity.signature)
  const [previewCompanyId, setPreviewCompanyId] = useState(identity.companyId ?? '')
  const { data } = useQuery({ queryKey: ['mail-companies'], queryFn: api.listMailCompanies })
  const save = useMutation({
    mutationFn: () =>
      api.updateMailIdentity(accountId, identity.id, {
        companyId: companyId || null,
        signatureMode: mode,
        signatureGreetingHtml: greeting,
      }),
    onSuccess: ({ identity: saved }) => {
      setGreeting(saved.signatureGreetingHtml ?? '')
      setPreview(saved.signature)
      setPreviewCompanyId(saved.companyId ?? '')
      qc.invalidateQueries({ queryKey: identitiesQueryKey(accountId) })
      notify.success('Signatur gespeichert')
    },
    onError: (error: Error) => notify.error(error.message),
  })
  return (
    <SidePanel
      open
      mobileFullHeight
      onClose={onClose}
      title="Signatur"
      footer={
        <Button className="w-full" disabled={save.isPending} onClick={() => save.mutate()}>
          Speichern
        </Button>
      }
    >
      <div className="space-y-3 pb-4">
        <div className="flex items-center gap-2 rounded-2xl border border-border-default p-3">
          <IdentityKindIcon kind={identity.kind} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">{identity.displayName ?? identity.address}</p>
            <p className="break-all text-xs text-text-tertiary">{identity.address}</p>
          </div>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] ${badge.ok ? 'bg-success/15 text-success' : 'bg-bg-overlay text-text-secondary'}`}
          >
            {badge.label}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <label className="shrink-0 text-sm font-semibold" htmlFor="identity-company">
            Firmenprofil
          </label>
          <select
            id="identity-company"
            className="min-w-0 flex-1 rounded-lg border border-border-default bg-bg-overlay p-2 text-sm"
            value={companyId}
            onChange={(event) => setCompanyId(event.target.value)}
          >
            <option value="">Keine Firma zugeordnet</option>
            {data?.companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-[1fr_1fr] rounded-lg border border-border-default text-xs">
          <button
            type="button"
            aria-pressed={mode === 'auto'}
            onClick={() => setMode('auto')}
            className={`no-min-tap rounded-l-lg py-2.5 ${mode === 'auto' ? 'bg-accent/15 text-accent' : 'text-text-secondary'}`}
          >
            Automatisch aus Firmenprofil
          </button>
          <button
            type="button"
            aria-pressed={mode === 'custom'}
            onClick={() => setMode('custom')}
            className={`no-min-tap rounded-r-lg py-2.5 ${mode === 'custom' ? 'bg-accent/15 text-accent' : 'text-text-secondary'}`}
          >
            Eigener Text
          </button>
        </div>
        <RichTextEditor
          value={greeting}
          onChange={setGreeting}
          label="Grußzeile bearbeiten"
          hint="nur Grußzeile bearbeitbar"
        >
          {mode === 'auto' && companyId === previewCompanyId && (
            <SignaturePreview signature={preview} />
          )}
        </RichTextEditor>
        {mode === 'auto' && companyId !== previewCompanyId && (
          <p className="text-xs text-text-tertiary">
            Speichern, um die Pflichtangaben dieser Firma zu sehen.
          </p>
        )}
        {mode === 'auto' && companyId === previewCompanyId && preview?.warning && (
          <div
            role="alert"
            className="flex gap-2 rounded-xl border border-warning/40 bg-warning/10 p-3 text-xs leading-relaxed"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <p className="text-text-secondary">
              <strong className="text-text-primary">{preview.warningLabel}.</strong>{' '}
              {data?.companies.find((company) => company.id === companyId)?.legalForm ===
              'einzelunternehmen'
                ? 'Für Einzelunternehmen ohne Handelsregister empfiehlt sich Name + ladungsfähige Anschrift (Anhang C). '
                : 'Bitte die fehlenden Angaben im Firmenprofil ergänzen. '}
              <Link
                to="/settings/mail-accounts"
                hash="mail-companies"
                onClick={onClose}
                className="font-semibold text-warning"
              >
                Firmenprofil ergänzen →
              </Link>
            </p>
          </div>
        )}
        <p className="text-xs leading-relaxed text-text-tertiary">
          Pflichtangaben gelten je Rechtsträger. Änderungen im Firmenprofil wirken auf alle Absender
          dieser Firma.
        </p>
      </div>
    </SidePanel>
  )
}
